import React from 'react';
import PropTypes from 'prop-types';
import { ThumbsUp, ThumbsDown, Hand, Clock } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { latestCompletedRound } from './CompletedInterviewCell';

// "Panel verdict" column — senior_level_flow's Completed ("Final Call") tab
// only, off the same row.completed_interview_rounds[].verdict_counts the
// Interviewed column reads (see CompletedInterviewCell.jsx): a per-round
// tally of how many panelists said advance ("go") / reject ("no-go") /
// hold, plus how many haven't voted yet.
//
// Matched to the reference design (screening-pipeline-v6.html's "Final
// call" tally) as closely as the data allows: go/no-go always render, even
// at zero, so the two pills stay in the same place across rows — a zero
// fades to a muted tone rather than disappearing. Hold and pending only
// render when they're non-zero, the same way the reference only shows its
// "pending" pill once someone is actually still owed.
//
// One real gap versus that reference: it names who dissented or who is
// still owed ("no-go from Deepak R.", "waiting on Deepak R.") straight off
// a per-panelist verdict list. verdict_counts is aggregate-only — no
// panelist names anywhere on this field — so a Split or Awaiting summary
// here states counts only, never names.
const TONE_CLASS = {
  success: 'text-ds-success',
  danger: 'text-ds-danger',
  warning: 'text-ds-warning',
};

// Pending panelists always win the headline — a call made while votes are
// still open is a different situation from one where everyone has weighed
// in, however lopsided the votes-so-far are.
const summarizeVerdicts = (vc) => {
  const advance = vc?.advance || 0;
  const reject = vc?.reject || 0;
  const hold = vc?.hold || 0;
  const pending = vc?.pending || 0;
  const total = advance + reject + hold + pending;
  if (!total) return null;

  if (pending > 0) {
    return { tone: 'warning', label: `Awaiting ${pending} of ${total}` };
  }
  if (!reject && !hold) {
    return {
      tone: 'success',
      label: 'Unanimous go',
      sub: `${total} of ${total} say go`,
    };
  }
  if (!advance && !hold) {
    return {
      tone: 'danger',
      label: 'Unanimous no-go',
      sub: `${total} of ${total} say no-go`,
    };
  }
  if (!advance && !reject) {
    return {
      tone: 'warning',
      label: 'Unanimous hold',
      sub: `${total} of ${total} say hold`,
    };
  }
  return {
    tone: 'warning',
    label: `Split ${advance}–${reject}`,
    sub: hold ? `${hold} hold` : undefined,
  };
};

const VerdictPill = ({ Icon, count, label, tone }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 whitespace-nowrap',
      count > 0 ? TONE_CLASS[tone] : 'text-muted-foreground/40'
    )}
  >
    <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
    <span className="font-mono text-[13px] font-semibold">{count}</span>
    <span className="text-[11px] font-medium">{label}</span>
  </span>
);

VerdictPill.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  count: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['success', 'danger', 'warning']).isRequired,
};

const PanelVerdictCell = ({ row }) => {
  const round = latestCompletedRound(row.completed_interview_rounds);
  const vc = round?.verdict_counts;
  const summary = summarizeVerdicts(vc);

  if (!round || !summary) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const { advance = 0, reject = 0, hold = 0, pending = 0 } = vc;

  return (
    <div className="min-w-0">
      <div
        className="flex items-center gap-3"
        role="img"
        aria-label={`${advance} go, ${reject} no-go, ${hold} hold, ${pending} pending`}
      >
        <VerdictPill
          Icon={ThumbsUp}
          count={advance}
          label="go"
          tone="success"
        />
        <VerdictPill
          Icon={ThumbsDown}
          count={reject}
          label="no-go"
          tone="danger"
        />
        {hold > 0 && (
          <VerdictPill Icon={Hand} count={hold} label="hold" tone="warning" />
        )}
        {pending > 0 && (
          <VerdictPill
            Icon={Clock}
            count={pending}
            label="pending"
            tone="warning"
          />
        )}
      </div>
      <p
        className={cn(
          'text-xs mt-1 whitespace-nowrap font-medium',
          TONE_CLASS[summary.tone]
        )}
      >
        {summary.label}
        {summary.sub && (
          <span className="text-muted-foreground"> · {summary.sub}</span>
        )}
      </p>
    </div>
  );
};

PanelVerdictCell.propTypes = {
  row: PropTypes.shape({
    completed_interview_rounds: PropTypes.arrayOf(
      PropTypes.shape({
        round_number: PropTypes.number,
        verdict_counts: PropTypes.shape({
          advance: PropTypes.number,
          reject: PropTypes.number,
          hold: PropTypes.number,
          pending: PropTypes.number,
        }),
      })
    ),
  }).isRequired,
};

export const panelVerdictColumn = {
  key: 'panel_verdict',
  header: 'Panel verdict',
  className: 'text-sm',
  align: 'left',
  width: '210px',
  render: (_v, row) => <PanelVerdictCell row={row} />,
};

export default PanelVerdictCell;
